import { action, atom } from '@reatom/framework'

import type { AtomMut, Ctx } from '@reatom/framework'

type SubscriptionMeta = {
  key: string
  count: number
  subArgs: unknown
}

type SubscriptionArgs<SubArgs = unknown, UnsubArgs = unknown> = {
  subscribe: (ctx: Ctx, args: SubArgs) => void
  unsubscribe: (ctx: Ctx, args: UnsubArgs) => void
  deriveKeyFromSubscribeArgs: (args: SubArgs) => string
  deriveKeyFromUnsubscribeArgs: (args: UnsubArgs) => string
}

export const reatomEventSubscription = <SubArgs, UnsubArgs>({
  subscribe: subscribeAction,
  unsubscribe: unsubscribeAction,
  deriveKeyFromSubscribeArgs,
  deriveKeyFromUnsubscribeArgs,
}: SubscriptionArgs<SubArgs, UnsubArgs>) => {
  const subscriptionMeta = new Map<string, AtomMut<SubscriptionMeta>>()

  const mkMetaAtom = (
    key: string,
    subArgs: unknown,
  ): AtomMut<SubscriptionMeta> => {
    // eslint-disable-next-line @reatom/unit-naming-rule
    const metaAtom = atom({
      key,
      count: 0,
      subArgs,
    })
    subscriptionMeta.set(key, metaAtom)
    return metaAtom
  }

  const subscribe = action((ctx, args: SubArgs) => {
    const key = deriveKeyFromSubscribeArgs(args)
    const metaAtom = subscriptionMeta.get(key) ?? mkMetaAtom(key, args)
    const meta = ctx.get(metaAtom)
    if (meta.count === 0) {
      subscribeAction(ctx, args)
    }

    metaAtom(ctx, (prev) => ({
      key,
      count: prev.count + 1,
      subArgs: args,
    }))
  }, 'subscribe')

  const unsubscribe = action((ctx, args: UnsubArgs) => {
    const key = deriveKeyFromUnsubscribeArgs(args)
    const metaAtom = subscriptionMeta.get(key) ?? mkMetaAtom(key, args)
    const meta = ctx.get(metaAtom)
    if (meta.count <= 1) {
      unsubscribeAction(ctx, args)
      subscriptionMeta.delete(key)
      return
    }
    metaAtom(ctx, (prev) => ({
      ...prev,
      count: prev.count - 1,
    }))
  }, 'unsubscribe')

  const reemitSubscriptionsActions = action((ctx) => {
    for (const metaAtom of subscriptionMeta.values()) {
      const meta = ctx.get(metaAtom)
      if (meta.count > 0) {
        subscribeAction(ctx, meta.subArgs as SubArgs)
      }
    }
  }, 'reemitSubscriptionsActions')

  return {
    subscribe,
    unsubscribe,
    reemitSubscriptionsActions,
  }
}
